import * as React from 'react';
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Card,
  Divider,
  IconButton,
  Chip,
  Container
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DeleteSweepOutlined, Notifications, ClearOutlined } from '@mui/icons-material';
import { clearNotifications, dismissNotification, getAllNotifications, setNotificationMarker } from '../../controllers/notify/notifications';
import { UserContext } from '../../context/UserContext';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MainLoggedInLayout } from '../../components/layouts/main.loggedIn.layout';
import { useNavigate } from 'react-router';
import { removeHTMLtags } from '../../util/util';
import { useProfileComplete } from '../../hooks/useProfileComplete';

// Styled components for better layout
const NotificationCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    boxShadow: theme.shadows[4],
    backgroundColor: theme.palette.action.hover,
  }
}));

const NotificationListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2),
  alignItems: 'flex-start',
}));

const NotificationContent = styled(Box)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(1),
}));

const NotificationHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(0.5),
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',
  maxWidth: 400,
  margin: '32px auto 0',
  borderRadius: 12,
  background: 'linear-gradient(120deg,#f3f0ff,#fff0f6)',
  border: '1px solid rgba(115,41,130,0.15)',
}));

const HeaderContainer = styled(Box)(() => ({
  padding: '0 0 16px 0',
  marginBottom: 16,
}));

export const NotificationList = () => {
    const { userDetails } = React.useContext(UserContext);
    const navigate = useNavigate();
    const token = userDetails?.token ?? '';
    
    // Check profile completion and redirect if needed
    useProfileComplete(userDetails);
    
    const queryClient = useQueryClient();
    const { data, refetch } = useQuery({
        queryKey: ['getAllNotifications'],
        queryFn: () => getAllNotifications(token)
    });
    const notifications = Array.isArray(data) ? data : [];

    React.useEffect(() => {
        refetch();
    }, [refetch]);

    // Mark everything as read once per page visit, as soon as the list loads.
    const hasMarkedRead = React.useRef(false);
    React.useEffect(() => {
        if (hasMarkedRead.current || notifications.length === 0 || !token) return
        hasMarkedRead.current = true
        setNotificationMarker(token, notifications[0].id).then(() => {
            queryClient.invalidateQueries({ queryKey: ['getNotificationMarker'] })
        })
    }, [notifications, token, queryClient])

    const dismissMutation = useMutation({
        mutationFn: ({ id }) => dismissNotification(id, token),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['getAllNotifications'] }),
    })

    const clearMutation = useMutation({
        mutationFn: () => clearNotifications(token),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['getAllNotifications'] }),
    })

    const handleDismiss = (id, event) => {
        event.stopPropagation();
        dismissMutation.mutate({ id });
    };

    const formatNotificationType = (type) => {
        if (type.includes('admin')) {
            return `Admin Alert: ${type.split(".")[1]}`;
        }
        return `${type}ed you`;
    };

    const getNotificationTypeStyle = (type) => {
        const normalizedType = (type || '').toLowerCase();

        if (normalizedType.includes('admin')) {
            return { backgroundColor: '#ffebee', color: '#c62828', borderColor: '#c62828' };
        }
        if (normalizedType.includes('like') || normalizedType.includes('favourite')) {
            return { backgroundColor: '#e8f5e8', color: '#2e7d32', borderColor: '#2e7d32' };
        }
        if (normalizedType.includes('follow')) {
            return { backgroundColor: '#e3f2fd', color: '#1976d2', borderColor: '#1976d2' };
        }
        if (normalizedType.includes('comment') || normalizedType.includes('reply')) {
            return { backgroundColor: '#e1f5fe', color: '#0288d1', borderColor: '#0288d1' };
        }
        if (normalizedType.includes('mention')) {
            return { backgroundColor: '#fff3e0', color: '#f57c00', borderColor: '#f57c00' };
        }
        return { backgroundColor: '#f3e5f5', color: '#7b1fa2', borderColor: '#7b1fa2' };
    };

    const handleNotificationClick = (notify) => {
        if (notify?.status) {
            navigate(`/post/${notify.status.id}`);
        }
    };

    const handleProfileClick = (accountId, event) => {
        event.stopPropagation();
        navigate(`/profile/${accountId}`);
    };
    return (
        <MainLoggedInLayout>
            <Container maxWidth="md">
                <HeaderContainer>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <Typography variant="h5" fontWeight={800} sx={{
                                background: 'linear-gradient(90deg,#E40303,#732982)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}>
                                Notifications
                            </Typography>
                            {notifications.length > 0 && (
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: 22,
                                        px: 1.1,
                                        borderRadius: 50,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: '#fff',
                                        background: 'linear-gradient(90deg,#E40303,#732982)',
                                    }}
                                >
                                    {notifications.length}
                                </Box>
                            )}
                        </Box>
                        {notifications.length > 0 && (
                            <IconButton
                                onClick={() => clearMutation.mutate()}
                                disabled={clearMutation.isPending}
                                title="Clear all notifications"
                            >
                                <DeleteSweepOutlined sx={{ color: 'text.secondary' }} />
                            </IconButton>
                        )}
                    </Box>
                </HeaderContainer>

                {notifications.length > 0 ? (
                    <List sx={{ width: '100%', padding: 0 }}>
                        {notifications.map((notify, i) => (
                            <NotificationCard 
                                key={i} 
                                onClick={() => handleNotificationClick(notify)}
                                elevation={1}
                            >
                                <NotificationListItem>
                                    <ListItemAvatar>
                                        <Avatar 
                                            onClick={(e) => handleProfileClick(notify.account.id, e)}
                                            alt={notify.account.username} 
                                            src={notify.account.avatar}
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    transform: 'scale(1.05)',
                                                    transition: 'transform 0.2s ease-in-out',
                                                }
                                            }}
                                        />
                                    </ListItemAvatar>
                                    
                                    <NotificationContent>
                                        <NotificationHeader>
                                            <Box>
                                                <Typography 
                                                    variant="subtitle1" 
                                                    fontWeight="bold"
                                                    component="span"
                                                >
                                                    @{notify.account.username}
                                                </Typography>
                                                <Typography 
                                                    variant="body2" 
                                                    color="text.secondary"
                                                    component="span"
                                                    sx={{ ml: 1 }}
                                                >
                                                    {formatNotificationType(notify.type)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        height: 24,
                                                        px: 1,
                                                        borderRadius: '12px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        border: '1px solid',
                                                        ...getNotificationTypeStyle(notify.type)
                                                    }}
                                                >
                                                    {notify.type?.split('.')[0] || notify.type || 'notification'}
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleDismiss(notify.id, e)}
                                                    title="Delete notification"
                                                >
                                                    <ClearOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
                                                </IconButton>
                                            </Box>
                                        </NotificationHeader>
                                        
                                        {notify?.status?.content && (
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary"
                                                sx={{ 
                                                    mt: 1,
                                                    lineHeight: 1.5,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                }}
                                            >
                                                {removeHTMLtags(notify.status.content)}
                                            </Typography>
                                        )}
                                    </NotificationContent>
                                </NotificationListItem>
                                {i < notifications.length - 1 && <Divider />}
                            </NotificationCard>
                        ))}
                    </List>
                ) : (
                    <EmptyState>
                        <Notifications
                            sx={{
                                fontSize: 48,
                                color: '#732982',
                                mb: 1.5
                            }}
                        />
                        <Typography fontWeight={800} fontSize={18} gutterBottom>
                            No notifications yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            When you have new activity, it will appear here.
                        </Typography>
                    </EmptyState>
                )}
            </Container>
        </MainLoggedInLayout>
    );
}